import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/features/auth/store';
import { toast } from '@/lib/toast';

const baseURL = import.meta.env.VITE_API_URL ?? '';

export const api = axios.create({ baseURL, timeout: 15_000 });

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Silent access-token refresh ─────────────────────────────────────────────
// The access token is short-lived (15 min). Rather than dumping the user back
// to the login screen every time it expires, a 401 triggers ONE refresh using
// the stored refresh token (7 days, or 90 days when "remember me" was ticked)
// and the failed request is replayed. Only when the refresh itself fails do we
// actually log out. This is what makes "remember me" work — the long-lived
// refresh token was being issued but never used.
let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;
  try {
    // Bare axios (no interceptors) so a 401 here can't recurse into itself.
    const { data } = await axios.post<{
      accessToken: string;
      refreshToken: string;
      mustChangePassword?: boolean;
    }>(`${baseURL}/api/auth/refresh`, { refreshToken });
    useAuthStore.getState().setTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      mustChangePassword: data.mustChangePassword,
    });
    return data.accessToken;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';
    const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh');

    if (status === 401 && original && !isAuthCall && !original._retried) {
      const hasRefresh = useAuthStore.getState().refreshToken != null;
      if (hasRefresh) {
        original._retried = true;
        // Single-flight: concurrent 401s share one refresh, then all replay.
        refreshing ??= refreshAccessToken().finally(() => { refreshing = null; });
        const newToken = await refreshing;
        if (newToken) {
          // The request interceptor re-attaches the (now fresh) token on replay.
          return api(original);
        }
      }
      // No refresh token, or the refresh itself failed → the session is truly
      // over. Clear + notify (once).
      const wasLoggedIn = useAuthStore.getState().accessToken != null;
      useAuthStore.getState().clear();
      if (wasLoggedIn) {
        toast.error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่');
      }
    }
    return Promise.reject(error);
  },
);
